import moment from "moment";
import { Grey6 } from "styles/antTheme/Colors";

import { BTSelectItem, type IBaseEntity } from "types/apiResponse/apiResponse";
import { type PredecessorTypes } from "types/enum";

import { getCompliantTextColor } from "utilities/color/color";
import {
    getWorkdayExceptionRecurrences,
    type IWorkdayException,
    type IWorkdayExceptionRecurrences,
    type IWorkdays,
} from "utilities/date/date";
import { type IListRowData, ListEntity } from "utilities/list/list.types";

import {
    BuilderEntry,
    ExportHeader,
    JobEntry,
} from "entity/schedule/Calendar/CalendarList/CalendarList.api.types";
import { type IGanttTaskData } from "entity/schedule/Gantt/GanttChart/GanttChart";
import { type IScheduleCompleteListInfo } from "entity/schedule/Schedule/common/ScheduleCompleteColumn";
import { type IScheduleItemPopoverDisplay } from "entity/schedule/Schedule/common/ScheduleItemPopover/ScheduleItemPopover.types";
import { type IAssignedToData } from "entity/schedule/Schedule/Schedule.api.types";
import {
    type InlineEditEntryPoint,
    type InlineEditPhase,
    type InlineEditSchedule,
} from "entity/schedule/ScheduleList/ScheduleList.api.types";

import { type default as GanttJson } from "./Gantt.api.json";

type GanttEntityApiData = typeof GanttJson;
type GanttEntityScheduleApiData = (typeof GanttJson)["items"][0];
type GanttEntityLinkApiData = (typeof GanttJson)["links"][0];
type GanttEntityPredecessorApiData = (typeof GanttJson)["items"][0]["predecessors"][0];

export class GanttEntity extends ListEntity implements IScheduleCompleteListInfo {
    constructor(data: GanttEntityApiData) {
        super(data);
        this.cellCount = data.cellCount;
        this.canMarkComplete = data.canMarkComplete;
        this.listMetadata = data.listMetadata;
        this.taskData = {
            data: data.items ? data.items.map((x: any) => new GanttEntry(x)) : [],
            links: [],
            lastUpdatedDate: new Date(),
        };
        let linkId = 0;
        this.taskData.links = this.taskData.data.flatMap((x) =>
            x.predecessors.map(
                (p) =>
                    new GanttLink({
                        id: linkId++,
                        target: x.id,
                        source: p.predecessorId,
                        type: p.predecessorType,
                        lag: p.lag,
                    }),
            ),
        );

        this.startDate = moment(data.startDate).toDate();
        this.endDate = moment(data.endDate).toDate();
        this.isBaselineSet = data.isBaselineSet;
        this.isCalendarOffline = data.isCalendarOffline;
        this.canSetBaseline = data.canSetBaseline;
        this.canMarkComplete = data.canMarkComplete;
        this.workdayExceptions = getWorkdayExceptionRecurrences(
            data.workdayExceptions,
            data.workdays,
        );
        this.ganttWorkdayExceptions = data.workdayExceptions;
        this.workdays = data.workdays;
        this.exportHeader = data.exportHeader ? new ExportHeader(data.exportHeader) : undefined;
        this.jobs = data.jobs.map((j: any) => new JobEntry(j));
        this.builders = data.builders.map((b: any) => new BuilderEntry(b));
        this.canViewPhaseDetails = data.canViewPhaseDetails;
    }

    cellCount: number;
    startDate: Date;
    endDate: Date;
    canMarkComplete: boolean;
    taskData: IGanttTaskData;
    isBaselineSet: boolean;
    isCalendarOffline: boolean;
    canSetBaseline: boolean;
    workdayExceptions: IWorkdayExceptionRecurrences;
    ganttWorkdayExceptions: IWorkdayException[];
    workdays: IWorkdays;
    exportHeader?: ExportHeader;
    jobs: JobEntry[];
    builders: BuilderEntry[];
    canViewPhaseDetails: boolean;
}

export enum GanttItemType {
    Schedule = 1,
    Phase = 2,
    Job = 3,
}

export class GanttEntry
    implements IBaseEntity, IListRowData, InlineEditSchedule, IScheduleItemPopoverDisplay
{
    constructor(data: GanttEntityScheduleApiData) {
        this.id = data.id;
        this.rowNumber = data.rowNumber;
        this.jobId = data.jobId;
        this.builderId = data.builderId;

        this.text = data.title;
        this.start_date = moment(data.startDate).startOf("day").toDate();
        this.end_date = moment(data.endDate).endOf("day").toDate();
        this.duration = data.duration;
        this.enteredDuration = data.duration;

        this.predecessors = data.predecessors
            ? data.predecessors.map((x: any) => new GanttPredecessor(x))
            : [];
        this.color = data.color ?? Grey6;
        this.planned_start = data.baseStartDate
            ? moment(data.baseStartDate).startOf("day").toDate()
            : undefined;
        this.planned_end = data.baseEndDate
            ? moment(data.baseEndDate).endOf("day").toDate()
            : undefined;
        this.completed = data.isComplete;
        this.completedDate = data.completedDate ? moment(data.completedDate) : undefined;
        this.completedBy = data.completedBy;
        this.percentComplete = data.percentComplete;
        this.itemType = data.itemType;
        this.parent = data.parent ? data.parent.toString() : undefined; // gantt chart expects the parent id to be a string

        // Set inline edit fields
        this.startDate = moment(data.startDate);
        this.endDate = moment(data.endDate);
        this.assignees = data.assignees
            ? data.assignees.map((x: any) => new BTSelectItem<IAssignedToData>(x))
            : [];
        this.inEditMode = false;
        this.selectedAssignees = this.assignees.map((x) => Number(x.id));
        this.validMap = new Map<InlineEditEntryPoint, boolean>();

        this.startHours = this.startDate.hours();
        this.startMinutes = this.startDate.minutes();
        this.endHours = this.endDate.hours();
        this.endMinutes = this.endDate.minutes();

        // Job & Phases only
        if (this.itemType !== GanttItemType.Schedule) {
            this.completeTotal = data.completeTotal;
            this.totalItems = data.totalItems;
            this.open = true; // expand by default
            this.readonly = true; // disable editing
        }

        this.jobTitle = data.jobTitle;
        this.actualStart = this.startDate.toDate();
        this.actualEnd = this.endDate.toDate();
        this.hasHourlyStart = data.hasHourlyStart;
        this.hasHourlyEnd = data.hasHourlyEnd;
        this.title = this.text;
        this.textColor = getCompliantTextColor(this.color);
        this.showOnGantt = data.showOnGantt ?? true;
    }

    // IBaseEntity
    id: number;
    jobId: number;
    builderId: number;

    rowNumber?: number;
    text: string;
    start_date: Date;
    end_date: Date;
    planned_start?: Date;
    planned_end?: Date;
    duration: number;
    enteredDuration: number;
    predecessors: GanttPredecessor[];
    color: string;
    textColor: string;
    canMarkComplete: boolean;

    // IScheduleCompleteListInfo
    completed: boolean;
    completedDate?: moment.Moment;
    completedBy?: string;
    completeTotal?: number;
    totalItems?: number;
    percentComplete: number;

    // InlineEditSchedule
    phase?: InlineEditPhase | undefined;
    selectedAssignees: number[];
    startDate: moment.Moment;
    endDate: moment.Moment;
    inlineEditAssignees: BTSelectItem<IAssignedToData>[];
    inEditMode: boolean;
    assignees: BTSelectItem<IAssignedToData>[];
    validMap: Map<InlineEditEntryPoint, boolean>;

    startHours: number;
    startMinutes: number;
    endHours: number;
    endMinutes: number;
    itemType: GanttItemType;
    parent?: string;
    open?: boolean;
    readonly: boolean;

    // ScheduleItemPopoverDisplay
    hasHourlyStart: boolean;
    hasHourlyEnd: boolean;
    actualStart: Date;
    actualEnd: Date;
    title: string;
    jobTitle: string;
    showOnGantt: boolean;
}

export class GanttLink {
    constructor(data: GanttEntityLinkApiData) {
        this.id = data.id;
        this.source = data.source;
        this.target = data.target;
        this.type = data.type;
        this.lag = data.lag;
    }

    id: number;
    source: number;
    target: number;
    type: PredecessorTypes;
    lag: number;
}

export class GanttPredecessor {
    constructor(data: GanttEntityPredecessorApiData) {
        this.linkVisible = data.linkVisible;
        this.predecessorId = data.predecessorId;
        this.scheduleId = data.scheduleId;
        this.predecessorType = data.predecessorType;
        this.lag = data.lag;
    }

    linkVisible: boolean;
    predecessorId: number;
    scheduleId: number;
    predecessorType: PredecessorTypes;
    lag: number;
}

export interface ICreateLinkRequest {
    item: number;
    itemStart: moment.Moment;
    pred: number;
    predStart: moment.Moment;
    predEnd: moment.Moment;
    predecessorType: PredecessorTypes;
    jobId: number;
    convertLinksToUtc: boolean;
}
